/**
 * Post Moderation Tools Component
 * 
 * Shows moderation actions (edit, delete) for individual posts
 */

import React from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical, Edit, Trash2 } from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'
import {
  FORUM_VIEW_STAFF_TOOLS,
  FORUM_EDIT_POSTS,
  FORUM_DELETE_POSTS,
} from '@/lib/constants/staff-permissions'

interface PostModToolsProps {
  postId: string
  isOwnPost: boolean
  onEdit: () => void
  onDelete: () => void
}

export function PostModTools({
  postId,
  isOwnPost,
  onEdit,
  onDelete,
}: PostModToolsProps) {
  const { hasPermission } = usePermissions()

  // Check permissions
  const canViewStaffTools = hasPermission(FORUM_VIEW_STAFF_TOOLS)
  const canEditPosts = hasPermission(FORUM_EDIT_POSTS)
  const canDeletePosts = hasPermission(FORUM_DELETE_POSTS)

  // Show menu if user owns the post OR has staff permissions
  const showEditOption = isOwnPost || canEditPosts
  const showDeleteOption = isOwnPost || canDeletePosts

  // Don't render if no actions available
  if (!showEditOption && !showDeleteOption) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {showEditOption && (
          <DropdownMenuItem onClick={onEdit} className="cursor-pointer">
            <Edit className="w-4 h-4 mr-2 text-primary" />
            <span>Editar Post</span>
          </DropdownMenuItem>
        )}

        {showDeleteOption && (
          <>
            {showEditOption && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={onDelete}
              className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              <span>Deletar Post</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
